import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FaEye, FaEyeSlash } from 'react-icons/fa6';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useNavigate } from 'react-router-dom';
import GoogleLoginButton from '../components/googleLoginButton/GoogleLoginButton.tsx';
import Select from '../components/select/Select.tsx';
import { UserInfo, useUserContext } from '../user/context/UserContext.tsx';
import { cities, HOST } from '../utils/constants.ts';
import { useLoadingContext } from './context/LoadingContext.tsx';
import './login.scss';

type LoginForm = {
  email: string;
  password: string;
};

type RegisterForm = {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  address: string;
  city: string;
  phone: string;
  description: string;
};

const PasswordInput = ({
  id,
  placeholder,
  value,
  onChange,
}: {
  id: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  return (
    <div style={{ position: 'relative', marginBottom: '15px' }}>
      <input
        id={id}
        type={isPasswordVisible ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="input-button"
        style={{ paddingRight: '40px' }}
      />
      <span
        onClick={() => setIsPasswordVisible(!isPasswordVisible)}
        className="password-toggle"
      >
        {isPasswordVisible ? (
          <FaEye className="password-icon" />
        ) : (
          <FaEyeSlash className="password-icon" />
        )}
      </span>
    </div>
  );
};

const Login: React.FC = () => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [loginForm, setLoginForm] = useState<LoginForm>({
    email: '',
    password: '',
  });
  const [registerForm, setRegisterForm] = useState<RegisterForm>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    address: '',
    city: '',
    phone: '',
    description: '',
  });

  const { showLoading, hideLoading } = useLoadingContext();
  const { setUserInfo } = useUserContext();
  const navigate = useNavigate();

  const handleRegister = () => {
    setIsRegistering(true);
  };

  const login = async () => {
    const url = `${HOST}/auth/login`;
    try {
      showLoading();
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm),
      });

      if (!response.ok) {
        let errorMessage = 'Error al iniciar sesión';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage =
            'El servidor no está disponible. Intenta de nuevo más tarde.';
        }
        throw new Error(errorMessage);
      }

      const userInfo: UserInfo = await response.json();

      if (!userInfo.user.isVerified) {
        toast.error('Debes verificar tu cuenta antes de iniciar sesión.', {
          duration: 4000,
        });
        return;
      }

      setUserInfo(userInfo);
      localStorage.setItem('token', userInfo.token);
      localStorage.setItem('userInfo', JSON.stringify(userInfo.user));
      hideLoading();
      navigate(`/`);
    } catch (error: any) {
      toast.error(`Error al intentar loguearse, ${error.message}`, {
        duration: 4000,
      });
    } finally {
      hideLoading();
    }
  };

  const register = async () => {
    showLoading();
    const url = `${HOST}/auth/register`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerForm),
      });

      if (!response.ok) {
        let errorMessage = 'Error al registrarse';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage =
            'El servidor no está disponible. Intenta de nuevo más tarde.';
        }
        throw new Error(errorMessage);
      }

      toast.success(
        <div>
          Se envió un mail con el link de activación de tu cuenta, revisa tu
          carpeta de <strong>spam</strong>.
        </div>,
        { duration: 10000 }
      );

      setIsRegistering(false);
    } catch (error: any) {
      toast.error(`Error al registrarse, ${error.message}`, { duration: 4000 });
    } finally {
      hideLoading();
    }
  };

  const validateArgentinianPhone = (phone: string) => {
    const cleanedPhone = phone.replace(/[\s()-]/g, '');
    const regex = /^(\+?54)\d{10}$/;
    return regex.test(cleanedPhone);
  };

  const handleLogin = (event: React.FormEvent) => {
    event.preventDefault();
    login();
  };

  const handleRegisterSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (registerForm.password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (!validateArgentinianPhone(registerForm.phone)) {
      toast.error(
        'Por favor, ingresa un número de teléfono válido de Argentina.'
      );
      return;
    }
    register();
  };

  const goHome = () => {
    navigate('/');
  };

  return (
    <>
      <div className="auth-form-title">
        <h2 className="auth-form-title__heading">
          {isRegistering ? 'Crear cuenta' : 'Iniciar sesión'}
        </h2>
        <p className="auth-form-title__sub">
          {isRegistering
            ? 'Llena los datos para crear tu cuenta'
            : 'Ingresa tus credenciales'}
        </p>
      </div>
      <form onSubmit={isRegistering ? handleRegisterSubmit : handleLogin}>
        {isRegistering ? (
          <>
            <label className="label" htmlFor="email">
              Correo electrónico
            </label>
            <input
              id="email"
              type="email"
              placeholder="Email"
              required
              value={registerForm.email}
              onChange={(e) =>
                setRegisterForm({ ...registerForm, email: e.target.value })
              }
              className="input-button"
            />

            <label className="label" htmlFor="password">
              Contraseña
            </label>
            <PasswordInput
              id="password"
              placeholder="Contraseña"
              value={registerForm.password}
              onChange={(e) =>
                setRegisterForm({ ...registerForm, password: e.target.value })
              }
            />
            <span className="helper-text">mínimo 8 caracteres.</span>

            <label className="label" htmlFor="confirmPassword">
              Repetir Contraseña
            </label>
            <PasswordInput
              id="confirmPassword"
              placeholder="Repetir Contraseña"
              value={registerForm.confirmPassword}
              onChange={(e) =>
                setRegisterForm({
                  ...registerForm,
                  confirmPassword: e.target.value,
                })
              }
            />

            <label className="label" htmlFor="name">
              Nombre completo
            </label>
            <input
              id="name"
              type="text"
              placeholder="Nombre completo"
              required
              value={registerForm.name}
              onChange={(e) =>
                setRegisterForm({ ...registerForm, name: e.target.value })
              }
              className="input-button"
            />

            <label className="label" htmlFor="address">
              Dirección
            </label>
            <input
              id="address"
              type="text"
              placeholder="Dirección"
              required
              value={registerForm.address}
              onChange={(e) =>
                setRegisterForm({ ...registerForm, address: e.target.value })
              }
              className="input-button"
            />

            <label className="label" htmlFor="city">
              Ciudad
            </label>
            <Select
              required
              placeholder="Selecciona tu ciudad"
              options={cities}
              value={cities.find((city) => city.value === registerForm.city)}
              isSearchable={false}
              onChange={(selectedOption: any) =>
                setRegisterForm({
                  ...registerForm,
                  city: selectedOption?.value || '',
                })
              }
            />
            <span style={{ marginTop: '2px' }} className="helper-text">
              Ciudad a donde se enviarán los pedidos
            </span>
            {/* <input
                id="city"
                type="text"
                placeholder="Ciudad"
                required
                value={registerForm.city}
                onChange={(selectedOption: any) =>
                  setRegisterForm({ ...registerForm, city: selectedOption?.value || '' })
                }
                className="input-button"
              /> */}

            <label className="label" htmlFor="phone">
              Teléfono
            </label>
            <PhoneInput
              placeholder="Ingresa tu número de teléfono"
              value={registerForm.phone || '+54 (11)'}
              onChange={(e) => setRegisterForm({ ...registerForm, phone: e })}
              country={'ar'}
              enableAreaCodes
              enableLongNumbers
              preferredCountries={['ar']}
              inputProps={{ required: true }}
            />

            <div className="margin-top-15">
              <button type="submit" className="primary-button">
                Registrarse
              </button>
            </div>
          </>
        ) : (
          <>
            <label className="label" htmlFor="loginEmail">
              Correo electrónico
            </label>
            <input
              id="loginEmail"
              type="email"
              placeholder="Email"
              required
              value={loginForm.email}
              onChange={(e) =>
                setLoginForm({ ...loginForm, email: e.target.value })
              }
              className="input-button"
            />

            <label className="label" htmlFor="loginPassword">
              Contraseña
            </label>
            <PasswordInput
              id="loginPassword"
              placeholder="Contraseña"
              value={loginForm.password}
              onChange={(e) =>
                setLoginForm({ ...loginForm, password: e.target.value })
              }
            />

            <p className="margin-top-8 margin-bottom-8 text-center">
              <span
                onClick={() => navigate('/forgot-password')}
                className="text-link"
              >
                ¿Olvidaste tu contraseña?
              </span>
            </p>

            <div className="container-buttons">
              <div className="margin-top">
                <button type="submit" className="primary-button">
                  Iniciar Sesión
                </button>
              </div>
              <div className="margin-top">
                <button onClick={goHome} className="secondary-button-login">
                  Ver Productos
                </button>
              </div>
            </div>

            <p className="text-center margin-top">
              <span className="separator-text">O continuar con:</span>
            </p>

            <div className="margin-top-8 margin-bottom-8">
              <GoogleLoginButton />
            </div>
          </>
        )}

        <p className="text-center auth-switch-text">
          {isRegistering ? (
            <>
              <span>¿Ya tienes cuenta? </span>
              <span
                onClick={() => setIsRegistering(false)}
                className="text-link"
              >
                Inicia sesión
              </span>
            </>
          ) : (
            <>
              <span>¿No tienes cuenta? </span>
              <span onClick={handleRegister} className="text-link">
                Regístrate
              </span>
            </>
          )}
        </p>

        {/* Información de contacto - solo en login */}
        {!isRegistering && (
          <div className="auth-contact">
            <p className="auth-contact__title">¿Necesitás ayuda?</p>
            <div className="auth-contact__list">
              <div className="auth-contact__item">
                <span className="auth-contact__icon">📧</span>
                <a
                  href="mailto:rapifruticonsultas@gmail.com"
                  className="auth-contact__link"
                >
                  rapifruticonsultas@gmail.com
                </a>
              </div>
              <div className="auth-contact__item">
                <span className="auth-contact__icon">📞</span>
                <a href="tel:+5491140490058" className="auth-contact__link">
                  +54 9 11 4049-0058
                </a>
              </div>
              <div className="auth-contact__item">
                <span className="auth-contact__icon">📸</span>
                <a
                  href="https://instagram.com/rapifruti"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="auth-contact__link"
                >
                  @rapifruti
                </a>
              </div>
            </div>
          </div>
        )}
      </form>
    </>
  );
};

export default Login;
